/* =============================================================================
   Card Product Luxury — JS Controller
   - Reveal on scroll (IntersectionObserver, staggered via CSS var)
   - Wishlist toggle (localStorage)
   - Quick add (AJAX /cart/add.js + cart-drawer-premium section render)
   - Quick view trigger (delegated → <luxury-quick-view>)
   - No Dawn dependencies. No reloads. No redirects.
   ============================================================================= */
(function () {
  'use strict';

  const WISHLIST_KEY = 'lux_wishlist_v1';
  const QV_SECTION_ID = 'quick-view-premium';

  /* ---------- helpers ---------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); }
    catch (_) { return []; }
  }
  function setWishlist(list) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); } catch (_) {}
  }

  function hydrateWishlist(root) {
    const list = getWishlist();
    $all('[data-lux-wishlist]', root || document).forEach((btn) => {
      const id = btn.getAttribute('data-product-id');
      if (id && list.indexOf(id) > -1) btn.classList.add('is-active');
    });
  }

  /* ---------- reveal on scroll ---------- */
  const io = ('IntersectionObserver' in window)
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 })
    : null;

  function observeCards(root) {
    const cards = $all('.lux-card', root || document);
    cards.forEach((c) => {
      if (io) io.observe(c);
      else c.classList.add('is-in');
    });
  }

  /* ---------- quick add ---------- */
  function setActionLoading(btn, on) { btn.classList.toggle('is-loading', !!on); btn.disabled = !!on; }
  function setActionSuccess(btn, on) { btn.classList.toggle('is-added', !!on); }

  function getCartDrawer() {
    return document.querySelector('cart-drawer-premium');
  }

  function quickAdd(btn) {
    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    const drawer = getCartDrawer();
    const sectionsToRender = drawer ? drawer.getSectionsToRender() : [];
    const sectionIds = sectionsToRender.map((s) => s.id).join(',');

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    if (sectionIds) {
      body.append('sections', sectionIds);
      body.append('sections_url', window.location.pathname);
    }

    setActionLoading(btn, true);

    fetch(window.Shopify && window.Shopify.routes ? (window.Shopify.routes.root + 'cart/add.js') : '/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body,
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.status) {
          // Shopify error envelope
          showToast(res.description || res.message || 'Sorry, could not add to cart.', 'error');
          setActionLoading(btn, false);
          return;
        }
        setActionLoading(btn, false);
        setActionSuccess(btn, true);
        // Find the original label and swap text briefly
        const label = btn.querySelector('.lux-card__cta-label');
        const original = label ? label.textContent : '';
        if (label) label.textContent = 'Added';
        setTimeout(() => {
          setActionSuccess(btn, false);
          if (label) label.textContent = original;
        }, 1800);

        // Render sections into drawer + open it
        if (drawer && res.sections) {
          try { drawer.renderContents(res); }
          catch (e) { /* noop */ }
        }
        // Publish for any listener
        if (window.PUB_SUB_EVENTS && window.publish) {
          try {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'lux-card', cartData: res });
          } catch (e) {}
        }
        showToast('Added to your cart', 'success');
      })
      .catch(() => {
        setActionLoading(btn, false);
        showToast('Could not add to cart. Please try again.', 'error');
      });
  }

  /* ---------- toast ---------- */
  let toastEl = null;
  let toastTimer = null;
  function ensureToast() {
    if (toastEl) return toastEl;
    toastEl = document.createElement('div');
    toastEl.className = 'lux-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastEl);
    // styles injected once
    if (!document.getElementById('lux-toast-style')) {
      const s = document.createElement('style');
      s.id = 'lux-toast-style';
      s.textContent = `
        .lux-toast{position:fixed;left:50%;bottom:2.4rem;transform:translateX(-50%) translateY(20px);
          padding:1.1rem 1.8rem;border-radius:999px;font-size:1.15rem;letter-spacing:.18em;text-transform:uppercase;
          font-weight:500;color:#F3EEE7;background:rgba(7,14,16,.92);backdrop-filter:blur(14px);
          -webkit-backdrop-filter:blur(14px);border:1px solid rgba(184,134,11,.4);box-shadow:0 14px 40px -16px rgba(0,0,0,.6);
          opacity:0;pointer-events:none;z-index:9999;transition:opacity .35s ease,transform .35s ease;}
        .lux-toast.is-on{opacity:1;transform:translateX(-50%) translateY(0)}
        .lux-toast.is-error{border-color:rgba(200,80,80,.5)}
        @media (prefers-reduced-motion: reduce){.lux-toast{transition-duration:.001ms}}
      `;
      document.head.appendChild(s);
    }
    return toastEl;
  }
  function showToast(message, kind) {
    const el = ensureToast();
    el.textContent = message;
    el.classList.toggle('is-error', kind === 'error');
    requestAnimationFrame(() => el.classList.add('is-on'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-on'), 2400);
  }

  /* ---------- quick view dispatch ---------- */
  function openQuickView(opts) {
    const panel = document.querySelector('luxury-quick-view');
    if (!panel || typeof panel.openFor !== 'function') {
      // graceful fallback: navigate to product page
      if (opts && opts.url) window.location.href = opts.url;
      return;
    }
    panel.openFor(opts);
  }

  /* ---------- global delegated handler ---------- */
  document.addEventListener('click', (e) => {
    const wish = e.target.closest('[data-lux-wishlist]');
    if (wish) {
      e.preventDefault();
      const id = wish.getAttribute('data-product-id');
      if (!id) return;
      const list = getWishlist();
      const idx = list.indexOf(id);
      if (idx > -1) { list.splice(idx, 1); wish.classList.remove('is-active'); }
      else { list.push(id); wish.classList.add('is-active'); }
      setWishlist(list);
      // mirror across other cards with same product
      $all('[data-lux-wishlist][data-product-id="' + id + '"]').forEach((b) => {
        b.classList.toggle('is-active', list.indexOf(id) > -1);
      });
      return;
    }

    const qv = e.target.closest('[data-lux-quick-view]');
    if (qv) {
      e.preventDefault();
      openQuickView({
        handle: qv.getAttribute('data-product-handle'),
        url: qv.getAttribute('data-product-url'),
        focusCta: qv.getAttribute('data-focus-cta') === 'true',
        trigger: qv,
      });
      return;
    }

    const add = e.target.closest('[data-lux-quick-add]');
    if (add) {
      e.preventDefault();
      if (add.disabled || add.classList.contains('is-loading')) return;
      quickAdd(add);
      return;
    }
  });

  /* ---------- expose minimal API + boot ---------- */
  window.LuxCard = {
    refresh(root) { observeCards(root); hydrateWishlist(root); },
    showToast: showToast,
    openQuickView: openQuickView,
  };

  function boot() {
    observeCards();
    hydrateWishlist();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
